import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

// Types
export interface Module {
  id: number
  courseId: number
  title: string
  description: string
  order: number
  duration?: number
  status: 'draft' | 'published' | 'archived'
  publishedAt?: string
  createdAt: string
  updatedAt: string
  course?: {
    id: number
    title: string
    slug: string
    companyId?: string | null
    shared?: boolean
  }
  // Detalhe dos episódios vem de /episodes; aqui só usamos count via _count.
  episodes?: unknown[]
  _count?: {
    episodes: number
  }
}

export interface CreateModuleDTO {
  courseId: number
  title: string
  description: string
  order: number
  duration?: number
  status?: 'draft' | 'published' | 'archived'
  publishedAt?: string
}

export interface UpdateModuleDTO {
  title?: string
  description?: string
  order?: number
  duration?: number
  status?: 'draft' | 'published' | 'archived'
  publishedAt?: string
}

// Hooks
export function useAdminModules(courseId?: number) {
  return useQuery({
    queryKey: courseId ? ['admin-modules', courseId] : ['admin-modules'],
    queryFn: async () => {
      const url = courseId ? `/admin/modules?courseId=${courseId}` : '/admin/modules'
      const response = await api.get<Module[]>(url)
      return response.data
    },
  })
}

export function useModulesByCourse(courseId: number) {
  return useQuery({
    queryKey: ['modules-by-course', courseId],
    queryFn: async () => {
      const response = await api.get<Module[]>(`/modules/course/${courseId}`)
      return response.data
    },
    enabled: !!courseId,
  })
}

export function useCreateModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateModuleDTO) => {
      const response = await api.post<Module>('/modules', data)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-modules'] })
      queryClient.invalidateQueries({ queryKey: ['modules-by-course'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.refetchQueries({ queryKey: ['admin-modules'] })
      queryClient.refetchQueries({ queryKey: ['modules-by-course'] })
      queryClient.refetchQueries({ queryKey: ['modules'] })
    },
  })
}

export function useUpdateModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateModuleDTO }) => {
      const response = await api.put<Module>(`/modules/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-modules'] })
      queryClient.invalidateQueries({ queryKey: ['modules-by-course'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.refetchQueries({ queryKey: ['admin-modules'] })
      queryClient.refetchQueries({ queryKey: ['modules-by-course'] })
      queryClient.refetchQueries({ queryKey: ['modules'] })
    },
  })
}

export function useDeleteModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/modules/${id}`)
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-modules'] })
      queryClient.invalidateQueries({ queryKey: ['modules-by-course'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.refetchQueries({ queryKey: ['admin-modules'] })
      queryClient.refetchQueries({ queryKey: ['modules-by-course'] })
      queryClient.refetchQueries({ queryKey: ['modules'] })
    },
  })
}
